import React, { useState, useEffect } from 'react';
import { Link2, Sparkles, Trash2, Calendar, Tag, ExternalLink, Loader2, Brain, Copy, Check, RefreshCw, Edit3, Save, X, MessageSquare } from 'lucide-react';
import { API_CONFIG } from '../config/api';

interface ResearchLink {
  id: string;
  url: string;
  title: string;
  description?: string;
  aiExpansion?: string;
  tags: string[];
  createdAt: string;
  processedAt?: string;
}

interface Note {
  id: string;
  linkId: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
}

export default function ResearchLinks() {
  const [links, setLinks] = useState<ResearchLink[]>([]);
  const [newUrl, setNewUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ResearchLink | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [reprocessingIds, setReprocessingIds] = useState<Set<string>>(new Set());
  const [customPrompt, setCustomPrompt] = useState('');
  const [showCustomPrompt, setShowCustomPrompt] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, Note[]>>({});
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/research-links`);
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
      }
    } catch (error) {
      console.error('Error fetching links:', error);
    }
  };

  const handleAddLink = async () => {
    if (!newUrl.trim()) return;

    setIsProcessing(true);
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/research-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newUrl })
      });

      if (response.ok) {
        const newLink = await response.json();
        setLinks([newLink, ...links]);
        setNewUrl('');
        setSelectedLink(newLink);
      }
    } catch (error) {
      console.error('Error adding link:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/research-links/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setLinks(links.filter(link => link.id !== id));
        if (selectedLink?.id === id) {
          setSelectedLink(null);
        }
      }
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleReprocess = async (id: string, useCustomPrompt: boolean = false) => {
    setReprocessingIds(new Set([...reprocessingIds, id]));
    
    try {
      const body: any = {};
      if (useCustomPrompt && customPrompt) {
        body.customPrompt = customPrompt;
      }
      
      const response = await fetch(`${API_CONFIG.API_URL}/research-links/${id}/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        // Poll for updates
        setTimeout(() => {
          fetchLinks();
          setReprocessingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error reprocessing link:', error);
      setReprocessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
    
    setShowCustomPrompt(null);
    setCustomPrompt('');
  };

  const saveNote = (linkId: string) => {
    if (!newNote.trim()) return;
    
    const note: Note = {
      id: crypto.randomUUID(),
      linkId,
      content: newNote,
      createdAt: new Date().toISOString()
    };
    
    setNotes(prev => ({
      ...prev,
      [linkId]: [...(prev[linkId] || []), note]
    }));
    
    setNewNote('');
    
    // In a real app, save to backend
    localStorage.setItem(`notes_${linkId}`, JSON.stringify([...(notes[linkId] || []), note]));
  };

  const updateNote = (linkId: string, noteId: string) => {
    setNotes(prev => ({
      ...prev,
      [linkId]: prev[linkId].map(note => 
        note.id === noteId 
          ? { ...note, content: editingNoteContent, updatedAt: new Date().toISOString() }
          : note
      )
    }));
    
    setEditingNote(null);
    setEditingNoteContent('');
    
    // Save to localStorage
    localStorage.setItem(`notes_${linkId}`, JSON.stringify(notes[linkId]));
  };

  const deleteNote = (linkId: string, noteId: string) => {
    setNotes(prev => ({
      ...prev,
      [linkId]: prev[linkId].filter(note => note.id !== noteId)
    }));
    
    // Save to localStorage
    const updatedNotes = notes[linkId].filter(note => note.id !== noteId);
    localStorage.setItem(`notes_${linkId}`, JSON.stringify(updatedNotes));
  };

  // Load notes from localStorage on mount
  useEffect(() => {
    links.forEach(link => {
      const savedNotes = localStorage.getItem(`notes_${link.id}`);
      if (savedNotes) {
        setNotes(prev => ({
          ...prev,
          [link.id]: JSON.parse(savedNotes)
        }));
      }
    });
  }, [links]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Brain className="w-10 h-10 text-indigo-600" />
            Research Links
          </h1>
          <p className="text-gray-600">Drop links, get AI-powered insights and expansions</p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex gap-3">
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isProcessing && handleAddLink()}
              placeholder="Paste a link to explore..."
              className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isProcessing}
            />
            <button
              onClick={handleAddLink}
              disabled={isProcessing || !newUrl.trim()}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Add & Expand
                </>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Links List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Your Research</h2>
            {links.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                <Link2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No links yet. Add your first research link above!</p>
              </div>
            ) : (
              links.map(link => (
                <div
                  key={link.id}
                  onClick={() => setSelectedLink(link)}
                  className={`bg-white rounded-xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    selectedLink?.id === link.id ? 'ring-2 ring-indigo-500 shadow-lg' : 'shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-gray-900 flex-1 pr-2">
                      {link.title || 'Processing...'}
                    </h3>
                    <div className="flex items-center gap-2">
                      {reprocessingIds.has(link.id) ? (
                        <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCustomPrompt(link.id);
                          }}
                          className="text-gray-400 hover:text-indigo-600 transition-colors"
                          title="Re-process with AI"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(link.id);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Custom prompt dialog */}
                  {showCustomPrompt === link.id && (
                    <div className="mb-3 p-3 bg-indigo-50 rounded-lg" onClick={(e) => e.stopPropagation()}>
                      <div className="text-sm font-medium text-gray-700 mb-2">Custom AI Prompt (optional)</div>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="e.g., Focus on technical implementation details..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={2}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleReprocess(link.id, true)}
                          className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                        >
                          Reprocess
                        </button>
                        <button
                          onClick={() => handleReprocess(link.id, false)}
                          className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                        >
                          Use Default
                        </button>
                        <button
                          onClick={() => {
                            setShowCustomPrompt(null);
                            setCustomPrompt('');
                          }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {link.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {link.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 hover:text-indigo-600 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Visit
                    </a>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(link.createdAt).toLocaleDateString()}
                    </span>
                    {link.tags.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {link.tags.length}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Selected Link Detail */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">AI Expansion</h2>
            {selectedLink ? (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedLink.title}</h3>
                  <a
                    href={selectedLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {selectedLink.url}
                  </a>
                </div>

                {selectedLink.aiExpansion ? (
                  <div className="space-y-4">
                    <div className="prose prose-sm max-w-none">
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-indigo-600" />
                            AI Analysis
                          </h4>
                          <button
                            onClick={() => copyToClipboard(selectedLink.aiExpansion || '', selectedLink.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {copiedId === selectedLink.id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <div className="text-gray-700 whitespace-pre-wrap">
                          {selectedLink.aiExpansion}
                        </div>
                      </div>
                    </div>

                    {selectedLink.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedLink.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Notes Section */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                        <MessageSquare className="w-4 h-4" />
                        Notes
                      </h4>
                      
                      {/* Add new note */}
                      <div className="mb-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && saveNote(selectedLink.id)}
                            placeholder="Add a note..."
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            onClick={() => saveNote(selectedLink.id)}
                            className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Display notes */}
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {notes[selectedLink.id]?.map(note => (
                          <div key={note.id} className="bg-gray-50 rounded-lg p-3">
                            {editingNote === note.id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editingNoteContent}
                                  onChange={(e) => setEditingNoteContent(e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => updateNote(selectedLink.id, note.id)}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingNote(null);
                                      setEditingNoteContent('');
                                    }}
                                    className="text-gray-600 hover:text-gray-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm text-gray-700">{note.content}</p>
                                <div className="flex justify-between items-center mt-2">
                                  <span className="text-xs text-gray-500">
                                    {new Date(note.createdAt).toLocaleString()}
                                    {note.updatedAt && ' (edited)'}
                                  </span>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setEditingNote(note.id);
                                        setEditingNoteContent(note.content);
                                      }}
                                      className="text-gray-400 hover:text-indigo-600"
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => deleteNote(selectedLink.id, note.id)}
                                      className="text-gray-400 hover:text-red-500"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )) || (
                          <p className="text-sm text-gray-500 italic">No notes yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-12 text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Processing link...
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center text-gray-400">
                <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a link to view AI expansion</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}